<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import type { OnboardingStep } from '@techsavanna/shared'
import Button from '../base/Button.vue'

interface Props {
  steps: OnboardingStep[]
  currentStepId: string
  completedSteps: string[]
  isLoading?: boolean
  canContinue?: boolean
  ctaText?: string
  hideFooter?: boolean
  hideBackButton?: boolean
  appId?: string
  appName?: string
  appTagline?: string
  appColor?: string
  appGradientFrom?: string
  appGradientTo?: string
  appLogo?: string
  appLogoWhite?: string
  isAuthenticated?: boolean
  userName?: string
  userInitials?: string
  siteUrl?: string
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
  canContinue: true,
  ctaText: '',
  hideFooter: false,
  hideBackButton: false,
  appId: 'pos',
  appName: 'Savanna',
  appTagline: 'Get started with your account',
  appColor: '#0f62ae',
  appGradientFrom: 'from-blue-600',
  appGradientTo: 'to-blue-500',
  appLogo: '',
  appLogoWhite: '',
  isAuthenticated: false,
  userName: '',
  userInitials: '',
  siteUrl: ''
})

const emit = defineEmits<{
  next: []
  back: []
  skip: []
  stepClick: [stepId: string]
  logout: []
  navigateDashboard: []
  navigateSite: []
}>()

// User dropdown state
const showUserMenu = ref(false)

function toggleUserMenu() {
  showUserMenu.value = !showUserMenu.value
}

function handleClickOutside(event: MouseEvent) {
  const target = event.target as HTMLElement
  if (!target.closest('.user-menu-container')) {
    showUserMenu.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

const currentStep = computed(() =>
  props.steps.find(s => s.id === props.currentStepId)
)

const currentStepIndex = computed(() =>
  props.steps.findIndex(s => s.id === props.currentStepId)
)

const isFirstStep = computed(() => currentStepIndex.value === 0)
const isLastStep = computed(() => currentStepIndex.value === props.steps.length - 1)

const progress = computed(() =>
  Math.round(((currentStepIndex.value + 1) / props.steps.length) * 100)
)

// Logo path with fallback
const logoPath = computed(() => {
  if (props.appLogo) return props.appLogo
  const logos: Record<string, string> = {
    pos: '/imgs/SavannapayPOS/logo/png/logo-no-background.png',
    people: '/imgs/WorkwiseHRM/logo/png/logo-no-background.png',
    elimu: '/imgs/ElimucoreLMS/logo/png/logo-no-background.png',
    erp: '/imgs/Savanna360ERP/logo/png/logo-no-background.png'
  }
  return logos[props.appId] || logos.pos
})

// Whether the current step is the setup/provisioning step
const isSetupStep = computed(() => props.currentStepId === 'setup')
</script>

<template>
  <div class="relative flex h-screen flex-col overflow-hidden bg-gray-50">
    <!-- Background Pattern -->
    <div class="pointer-events-none absolute inset-0 overflow-hidden">
      <!-- App-specific Background Pattern -->
      <svg class="absolute inset-0 h-full w-full" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <!-- POS: Grid lines -->
          <pattern v-if="appId === 'pos'" :id="`bg-pattern-${appId}`" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
            <path d="M60 0V60H0" fill="none" stroke="currentColor" stroke-width="0.5" class="text-blue-200" />
          </pattern>
          <!-- Elimu: Diagonal lines -->
          <pattern v-else-if="appId === 'elimu'" :id="`bg-pattern-${appId}`" x="0" y="0" width="28" height="28" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="28" stroke="currentColor" stroke-width="0.5" class="text-amber-200" />
          </pattern>
          <!-- ERP: Vertical lines -->
          <pattern v-else-if="appId === 'erp'" :id="`bg-pattern-${appId}`" x="0" y="0" width="120" height="120" patternUnits="userSpaceOnUse">
            <line x1="60" y1="0" x2="60" y2="120" stroke="currentColor" stroke-width="0.5" class="text-violet-200" />
          </pattern>
          <!-- People: Plus/cross -->
          <pattern v-else :id="`bg-pattern-${appId}`" x="0" y="0" width="72" height="72" patternUnits="userSpaceOnUse">
            <path d="M36 32v8M32 36h8" fill="none" stroke="currentColor" stroke-width="0.5" stroke-linecap="round" class="text-indigo-200" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" :fill="`url(#bg-pattern-${appId})`" />
      </svg>

      <!-- App-specific floating icons - POS -->
      <template v-if="appId === 'pos'">
        <!-- Credit Card -->
        <svg class="absolute left-[5%] top-[15%] size-20" :style="{ color: appColor }" fill="currentColor" fill-opacity="0.08" stroke="currentColor" stroke-opacity="0.15" stroke-width="0.5" viewBox="0 0 24 24">
          <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>
        </svg>
        <!-- Shopping Cart -->
        <svg class="absolute right-[8%] top-[20%] size-24" :style="{ color: appColor }" fill="currentColor" fill-opacity="0.08" stroke="currentColor" stroke-opacity="0.15" stroke-width="0.5" viewBox="0 0 24 24">
          <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
        </svg>
        <!-- Receipt -->
        <svg class="absolute bottom-[25%] left-[10%] size-18" :style="{ color: appColor }" fill="currentColor" fill-opacity="0.08" stroke="currentColor" stroke-opacity="0.15" stroke-width="0.5" viewBox="0 0 24 24">
          <path d="M4 2v20l3-2 3 2 3-2 3 2 3-2 3 2V2l-3 2-3-2-3 2-3-2-3 2-3-2z"/><line x1="8" y1="8" x2="16" y2="8"/><line x1="8" y1="12" x2="14" y2="12"/>
        </svg>
        <!-- Barcode -->
        <svg class="absolute bottom-[35%] right-[12%] size-16" :style="{ color: appColor }" fill="currentColor" fill-opacity="0.08" stroke="currentColor" stroke-opacity="0.15" stroke-width="0.5" viewBox="0 0 24 24">
          <rect x="2" y="4" width="2" height="16"/><rect x="6" y="4" width="1" height="16"/><rect x="9" y="4" width="2" height="16"/><rect x="13" y="4" width="1" height="16"/><rect x="16" y="4" width="3" height="16"/><rect x="21" y="4" width="1" height="16"/>
        </svg>
      </template>

      <!-- App-specific floating icons - People/HRM -->
      <template v-else-if="appId === 'people'">
        <!-- Users -->
        <svg class="absolute left-[5%] top-[15%] size-20" :style="{ color: appColor }" fill="currentColor" fill-opacity="0.08" stroke="currentColor" stroke-opacity="0.15" stroke-width="0.5" viewBox="0 0 24 24">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
        <!-- Calendar -->
        <svg class="absolute right-[8%] top-[20%] size-24" :style="{ color: appColor }" fill="currentColor" fill-opacity="0.08" stroke="currentColor" stroke-opacity="0.15" stroke-width="0.5" viewBox="0 0 24 24">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
        <!-- Clock -->
        <svg class="absolute bottom-[25%] left-[10%] size-18" :style="{ color: appColor }" fill="currentColor" fill-opacity="0.08" stroke="currentColor" stroke-opacity="0.15" stroke-width="0.5" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
        </svg>
        <!-- ID Badge -->
        <svg class="absolute bottom-[35%] right-[12%] size-16" :style="{ color: appColor }" fill="currentColor" fill-opacity="0.08" stroke="currentColor" stroke-opacity="0.15" stroke-width="0.5" viewBox="0 0 24 24">
          <rect x="4" y="2" width="16" height="20" rx="2"/><circle cx="12" cy="10" r="3"/><path d="M8 18h8"/>
        </svg>
      </template>

      <!-- App-specific floating icons - Elimu/LMS -->
      <template v-else-if="appId === 'elimu'">
        <!-- Book -->
        <svg class="absolute left-[5%] top-[15%] size-20" :style="{ color: appColor }" fill="currentColor" fill-opacity="0.08" stroke="currentColor" stroke-opacity="0.15" stroke-width="0.5" viewBox="0 0 24 24">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
        </svg>
        <!-- Graduation Cap -->
        <svg class="absolute right-[8%] top-[20%] size-24" :style="{ color: appColor }" fill="currentColor" fill-opacity="0.08" stroke="currentColor" stroke-opacity="0.15" stroke-width="0.5" viewBox="0 0 24 24">
          <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/>
        </svg>
        <!-- Pencil -->
        <svg class="absolute bottom-[25%] left-[10%] size-18" :style="{ color: appColor }" fill="currentColor" fill-opacity="0.08" stroke="currentColor" stroke-opacity="0.15" stroke-width="0.5" viewBox="0 0 24 24">
          <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
        </svg>
        <!-- Calculator -->
        <svg class="absolute bottom-[35%] right-[12%] size-16" :style="{ color: appColor }" fill="currentColor" fill-opacity="0.08" stroke="currentColor" stroke-opacity="0.15" stroke-width="0.5" viewBox="0 0 24 24">
          <rect x="4" y="2" width="16" height="20" rx="2"/><line x1="8" y1="6" x2="16" y2="6"/><line x1="8" y1="10" x2="8" y2="10"/><line x1="12" y1="10" x2="12" y2="10"/><line x1="16" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="8" y2="14"/><line x1="12" y1="14" x2="12" y2="14"/><line x1="16" y1="14" x2="16" y2="14"/><line x1="8" y1="18" x2="8" y2="18"/><line x1="12" y1="18" x2="12" y2="18"/><line x1="16" y1="18" x2="16" y2="18"/>
        </svg>
      </template>

      <!-- App-specific floating icons - ERP -->
      <template v-else-if="appId === 'erp'">
        <!-- Bar Chart -->
        <svg class="absolute left-[5%] top-[15%] size-20" :style="{ color: appColor }" fill="currentColor" fill-opacity="0.08" stroke="currentColor" stroke-opacity="0.15" stroke-width="0.5" viewBox="0 0 24 24">
          <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
        </svg>
        <!-- Building -->
        <svg class="absolute right-[8%] top-[20%] size-24" :style="{ color: appColor }" fill="currentColor" fill-opacity="0.08" stroke="currentColor" stroke-opacity="0.15" stroke-width="0.5" viewBox="0 0 24 24">
          <rect x="4" y="2" width="16" height="20"/><line x1="9" y1="22" x2="9" y2="12"/><line x1="15" y1="22" x2="15" y2="12"/><line x1="9" y1="6" x2="9" y2="6"/><line x1="15" y1="6" x2="15" y2="6"/><line x1="9" y1="10" x2="9" y2="10"/><line x1="15" y1="10" x2="15" y2="10"/>
        </svg>
        <!-- Gears -->
        <svg class="absolute bottom-[25%] left-[10%] size-18" :style="{ color: appColor }" fill="currentColor" fill-opacity="0.08" stroke="currentColor" stroke-opacity="0.15" stroke-width="0.5" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
        </svg>
        <!-- Pie Chart -->
        <svg class="absolute bottom-[35%] right-[12%] size-16" :style="{ color: appColor }" fill="currentColor" fill-opacity="0.08" stroke="currentColor" stroke-opacity="0.15" stroke-width="0.5" viewBox="0 0 24 24">
          <path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/>
        </svg>
      </template>

      <!-- Gradient Orbs -->
      <div
        class="absolute -right-32 -top-32 size-96 rounded-full opacity-20 blur-3xl"
        :style="{ background: `radial-gradient(circle, ${appColor}40 0%, transparent 70%)` }"
      />
      <div
        class="absolute -bottom-32 -left-32 size-96 rounded-full opacity-20 blur-3xl"
        :style="{ background: `radial-gradient(circle, ${appColor}30 0%, transparent 70%)` }"
      />
      <div
        class="absolute right-1/4 top-1/3 size-64 rounded-full opacity-10 blur-3xl"
        :style="{ background: `radial-gradient(circle, ${appColor}50 0%, transparent 70%)` }"
      />
    </div>

    <!-- Header -->
    <header class="relative z-30 shrink-0 border-b border-gray-200/60 bg-white/60 backdrop-blur-md">
      <div class="mx-auto flex h-14 max-w-6xl items-center justify-between px-2 sm:px-3 lg:px-4">
        <a href="/" class="flex shrink-0 items-center">
          <img
            :src="logoPath"
            :alt="appName"
            class="h-8 max-w-[160px] w-auto object-contain"
          />
        </a>

        <!-- Step Progress (centered, hidden on mobile and during setup) -->
        <div v-if="!isSetupStep" class="hidden items-center sm:flex">
          <template v-for="(step, index) in steps" :key="step.id">
            <!-- Connector line between steps -->
            <div v-if="index > 0" class="relative mx-0.5 h-0.5 w-6 overflow-hidden rounded-full bg-gray-200 lg:w-8">
              <div
                class="absolute inset-y-0 left-0 rounded-full transition-all duration-700 ease-out"
                :style="{ width: index <= currentStepIndex ? '100%' : '0%', backgroundColor: appColor }"
              />
            </div>

            <!-- Step circle + label -->
            <div class="flex items-center gap-1.5">
              <div
                class="relative flex size-6 items-center justify-center rounded-full text-[10px] font-bold transition-all duration-300"
                :class="[
                  currentStepIndex === index
                    ? 'shadow-md'
                    : completedSteps.includes(step.id)
                      ? 'text-white'
                      : 'border border-blue-200 bg-blue-50 text-blue-400'
                ]"
                :style="currentStepIndex === index ? { backgroundColor: '#60a5fa', color: '#fff' } : completedSteps.includes(step.id) ? { backgroundColor: appColor } : {}"
              >
                <!-- Pulse ring on active step -->
                <span
                  v-if="currentStepIndex === index"
                  class="absolute inset-0 animate-ping rounded-full opacity-20"
                  :style="{ backgroundColor: appColor }"
                />
                <!-- Completed check icon -->
                <svg
                  v-if="completedSteps.includes(step.id) && currentStepIndex !== index"
                  class="size-3"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                </svg>
                <!-- Step number -->
                <span v-else class="relative">{{ index + 1 }}</span>
              </div>

              <!-- Step label (visible on larger screens) -->
              <span
                class="hidden text-[11px] font-medium transition-colors lg:inline"
                :class="[
                  currentStepIndex === index
                    ? 'text-gray-900'
                    : completedSteps.includes(step.id)
                      ? 'text-gray-600'
                      : 'text-gray-400'
                ]"
              >{{ step.title.split(' ').slice(0, 2).join(' ') }}</span>
            </div>
          </template>
        </div>

        <!-- Mobile progress: step counter + mini bar (hidden during setup) -->
        <div v-if="!isSetupStep" class="flex items-center gap-2 sm:hidden">
          <span class="text-[11px] font-medium text-gray-500">{{ currentStepIndex + 1 }}/{{ steps.length }}</span>
          <div class="h-1.5 w-16 overflow-hidden rounded-full bg-gray-200">
            <div
              class="h-full rounded-full transition-all duration-500"
              :style="{ width: `${progress}%`, backgroundColor: appColor }"
            />
          </div>
        </div>

        <!-- User dropdown (authenticated) -->
        <div v-if="isAuthenticated" class="user-menu-container relative shrink-0">
          <button
            class="flex cursor-pointer items-center gap-2.5 rounded-lg px-1.5 py-1 transition-all hover:bg-gray-100/80"
            @click.stop="toggleUserMenu"
          >
            <div
              class="flex size-7 items-center justify-center rounded-full ring-2 ring-white text-[11px] font-semibold text-white shadow-sm"
              :style="{ backgroundColor: appColor }"
            >
              {{ userInitials }}
            </div>
            <span class="hidden max-w-[100px] truncate text-[13px] font-medium text-gray-600 sm:inline">{{ userName }}</span>
            <svg class="size-3 text-gray-400/70 transition-transform" :class="{ 'rotate-180': showUserMenu }" viewBox="0 0 24 24" fill="currentColor">
              <path fill-rule="evenodd" d="M12.53 16.28a.75.75 0 0 1-1.06 0l-7.5-7.5a.75.75 0 0 1 1.06-1.06L12 14.69l6.97-6.97a.75.75 0 1 1 1.06 1.06l-7.5 7.5Z" clip-rule="evenodd"/>
            </svg>
          </button>

          <!-- Dropdown menu -->
          <Transition
            enter-active-class="transition duration-100 ease-out"
            enter-from-class="scale-95 opacity-0"
            enter-to-class="scale-100 opacity-100"
            leave-active-class="transition duration-75 ease-in"
            leave-from-class="scale-100 opacity-100"
            leave-to-class="scale-95 opacity-0"
          >
            <div
              v-if="showUserMenu"
              class="absolute right-0 top-full z-50 mt-1.5 w-56 origin-top-right overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg shadow-gray-200/50"
            >
              <div class="border-b border-gray-100 px-4 py-3">
                <p class="truncate text-sm font-medium text-gray-900">{{ userName }}</p>
              </div>
              <div class="py-1">
                <button
                  class="flex w-full cursor-pointer items-center gap-2.5 px-4 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50"
                  @click="emit('navigateDashboard'); showUserMenu = false"
                >
                  <svg class="size-4 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                    <path fill-rule="evenodd" d="M3 6a3 3 0 0 1 3-3h2.25a3 3 0 0 1 3 3v2.25a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V6Zm9.75 0a3 3 0 0 1 3-3H18a3 3 0 0 1 3 3v2.25a3 3 0 0 1-3 3h-2.25a3 3 0 0 1-3-3V6ZM3 15.75a3 3 0 0 1 3-3h2.25a3 3 0 0 1 3 3V18a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3v-2.25Zm9.75 0a3 3 0 0 1 3-3H18a3 3 0 0 1 3 3V18a3 3 0 0 1-3 3h-2.25a3 3 0 0 1-3-3v-2.25Z" clip-rule="evenodd"/>
                  </svg>
                  Dashboard
                </button>
                <button
                  v-if="siteUrl"
                  class="flex w-full cursor-pointer items-center gap-2.5 px-4 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50"
                  @click="emit('navigateSite'); showUserMenu = false"
                >
                  <svg class="size-4 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                    <path fill-rule="evenodd" d="M15.75 2.25H21a.75.75 0 0 1 .75.75v5.25a.75.75 0 0 1-1.5 0V4.81l-8.97 8.97a.75.75 0 0 1-1.06-1.06l8.97-8.97h-3.44a.75.75 0 0 1 0-1.5Zm-10.5 4.5a1.5 1.5 0 0 0-1.5 1.5v10.5a1.5 1.5 0 0 0 1.5 1.5h10.5a1.5 1.5 0 0 0 1.5-1.5V10.5a.75.75 0 0 1 1.5 0v8.25a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3V8.25a3 3 0 0 1 3-3h8.25a.75.75 0 0 1 0 1.5H5.25Z" clip-rule="evenodd"/>
                  </svg>
                  View My Site
                </button>
                <button
                  class="flex w-full cursor-pointer items-center gap-2.5 px-4 py-2 text-left text-sm text-red-600 transition-colors hover:bg-red-50"
                  @click="emit('logout'); showUserMenu = false"
                >
                  <svg class="size-4" viewBox="0 0 24 24" fill="currentColor">
                    <path fill-rule="evenodd" d="M16.5 3.75a1.5 1.5 0 0 1 1.5 1.5v13.5a1.5 1.5 0 0 1-1.5 1.5h-6a1.5 1.5 0 0 1-1.5-1.5V15a.75.75 0 0 0-1.5 0v3.75a3 3 0 0 0 3 3h6a3 3 0 0 0 3-3V5.25a3 3 0 0 0-3-3h-6a3 3 0 0 0-3 3V9A.75.75 0 0 0 9 9V5.25a1.5 1.5 0 0 1 1.5-1.5h6ZM5.78 8.47a.75.75 0 0 0-1.06 0l-3 3a.75.75 0 0 0 0 1.06l3 3a.75.75 0 0 0 1.06-1.06l-1.72-1.72H15a.75.75 0 0 0 0-1.5H4.06l1.72-1.72a.75.75 0 0 0 0-1.06Z" clip-rule="evenodd"/>
                  </svg>
                  Log out
                </button>
              </div>
            </div>
          </Transition>
        </div>

        <!-- Login link (not authenticated) -->
        <a
          v-else
          href="/login"
          class="shrink-0 text-sm text-gray-500 transition-colors hover:text-gray-900"
        >
          Already have an account? <span class="font-medium" :style="{ color: appColor }">Log in</span>
        </a>
      </div>
    </header>

    <!-- Main Content (scrollable) -->
    <div
      class="relative z-10 min-h-0 flex-1 overflow-y-auto"
      :class="isSetupStep ? 'flex items-center justify-center' : 'pb-20'"
    >
      <div class="mx-auto w-full px-4" :class="[isSetupStep ? 'max-w-xl' : 'max-w-md py-4 sm:py-6']">
        <!-- Card Container -->
        <div
          class="border border-gray-200/60 bg-white/70 backdrop-blur-md"
          :class="isSetupStep ? 'rounded-3xl p-6 sm:p-8' : 'rounded-xl p-4 shadow-lg shadow-gray-200/40 sm:p-6'"
        >
          <!-- Step Header -->
          <div v-if="!isSetupStep" class="-mx-4 -mt-1 mb-4 border-b border-gray-200/60 px-4 pb-3 sm:-mx-6 sm:-mt-2 sm:px-6">
            <h1 class="text-base font-semibold leading-tight text-gray-900">
              {{ currentStep?.title }}
            </h1>
            <p class="text-xs text-gray-500">
              {{ currentStep?.description }}
            </p>
          </div>

          <!-- Step Content -->
          <div>
            <slot :step="currentStep" />
          </div>

          <!-- Setup step footer -->
          <div v-if="isSetupStep" class="-mx-6 -mb-8 mt-6 rounded-b-3xl border-t border-gray-200/60 bg-gray-50/80 px-6 py-4 sm:-mx-8 sm:px-8">
            <div class="flex items-start gap-2.5">
              <svg class="mt-0.5 size-4 shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
              </svg>
              <p class="text-xs leading-relaxed text-gray-500">
                Your site details will be sent to your email. You can safely close this page and check your email later.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Fixed Footer Navigation -->
    <footer v-if="!hideFooter" class="relative z-10 shrink-0 bg-white/60 backdrop-blur-md">
      <!-- Segmented Progress Bar -->
      <div class="mx-auto flex max-w-md gap-1.5 px-4">
        <div
          v-for="(step, index) in steps"
          :key="step.id"
          class="h-[3px] flex-1 rounded-full transition-all duration-500 ease-out"
          :class="index <= currentStepIndex ? '' : 'bg-gray-200/60'"
          :style="index <= currentStepIndex ? { backgroundColor: appColor } : {}"
        />
      </div>
      <div class="mx-auto flex h-14 max-w-md items-center justify-between px-4">
        <div>
          <Button
            v-if="!isFirstStep && !hideBackButton"
            variant="ghost"
            size="sm"
            @click="$emit('back')"
          >
            <svg class="mr-1 size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
            </svg>
            Back
          </Button>
        </div>

        <div class="flex items-center gap-2">
          <Button
            v-if="currentStep && !currentStep.required"
            variant="ghost"
            size="sm"
            @click="$emit('skip')"
          >
            Skip
          </Button>
          <Button
            size="sm"
            :loading="isLoading"
            :disabled="!canContinue"
            @click="$emit('next')"
          >
            {{ ctaText || (isLastStep ? 'Complete Setup' : 'Continue') }}
            <svg v-if="!ctaText && !isLastStep" class="ml-1 size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </Button>
        </div>
      </div>
    </footer>
  </div>
</template>
